import {
  Sparkles,
  Calendar,
  TrendingUp,
  TrendingDown,
  Flag,
  AlertTriangle,
  Check,
  Target,
  BarChart3,
  Clock,
  CheckCircle2,
  Circle,
  Zap,
  Loader2,
  ListTodo,
  ArrowUpRight,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { ReactNode } from 'react';
import type { Tables } from '@/types/database.types';

// Live project dashboard rendered as the leftmost "Dashboard" tab on the
// project details page. All sections render real project data: hero
// header, KPIs (with kpi_history sparklines + trend pills), milestones
// gantt, risks, this-week priorities, recent wins, and executive summary.

/** Minimal shape ProjectDashboard needs to render an avatar — caller passes
 *  raw workspace member data and the dashboard derives initials and a
 *  deterministic color so the rest of the app doesn't need to care about
 *  presentation details. */
export interface DashboardMember {
  /** Stable id used to seed the avatar color so the same person always
   *  gets the same color across renders. user_id from workspace_users. */
  id: string;
  name: string | null;
  email: string;
}

/** Subset of the projects-table row shape that the dashboard reads from.
 *  Mirrors `Tables<'projects'>` field names so callers can pass the row
 *  directly (or build it from edited state) without translating shapes. */
export interface DashboardProject {
  name: string;
  description: string | null;
  external_id: string | null;
  overall_health: 'green' | 'yellow' | 'red';
  start_date: string | null;
  end_date: string | null;
  completion_percentage: number | null;
  executive_summary: string | null;
}

/** Time-series points for one KPI's sparkline. Sorted oldest → newest. */
// `id` is included so the Details pane can edit / delete individual rows.
// The dashboard itself only reads value + recorded_at — the id is ignored
// here, but tightly coupling the type keeps cross-component callsites
// honest about what they're working with.
export type KpiHistoryPoint = { id: string; value: number; recorded_at: string };

interface ProjectDashboardProps {
  /** Project row data (or an edit-state-derived view of it). Always reflects
   *  the current value of fields the user is editing in the Details tab so
   *  the dashboard stays in sync without a round-trip through the DB. */
  project: DashboardProject;
  /** Workspace members to show as a stack of avatars in the hero, plus
   *  used to resolve milestone/risk owner_id → display name. */
  members: DashboardMember[];
  /** Active KPIs for this project. Inactive KPIs are filtered out by the
   *  dashboard so callers don't need to pre-filter. */
  kpis: Tables<'kpis'>[];
  /** History points for each KPI keyed by kpi_id. Empty/missing entries
   *  cause the sparkline to render a 0 → current_value line. */
  kpiHistory: Map<string, KpiHistoryPoint[]>;
  /** Project tasks. The dashboard shows a summary + the next few to-dos;
   *  the full list lives in the Details tab. */
  tasks: Tables<'tasks'>[];
  /** Project milestones (chronological by due_date). */
  milestones: Tables<'milestones'>[];
  /** Project risks. Sorted server-side; dashboard renders top-N. */
  risks: Tables<'risks'>[];
  /** Status updates of type 'priority'. */
  priorities: Tables<'status_updates'>[];
  /** Status updates of type 'accomplishment'. */
  accomplishments: Tables<'status_updates'>[];
  /** True while a regeneration request is in flight — drives the button's
   *  disabled state and spinner. */
  isRegeneratingSummary: boolean;
  /** Click handler for the Regenerate button. Pass `null` to hide the
   *  button entirely (read-only viewers). */
  onRegenerateSummary: (() => void) | null;
  /** Navigate to a section's editor in the Details tab. Pass `undefined` for
   *  read-only viewers — section headers then render as plain, non-clickable
   *  text (same read-only signal as onRegenerateSummary). */
  onNavigateToSection?: (section: string) => void;
}

// A dashboard section heading. When onNavigate is provided (editors), the
// heading becomes a button that jumps to that section's editor in the Details
// tab. For read-only viewers onNavigate is undefined and it renders as plain
// text — the same read-only signal used to hide the Regenerate button.
function SectionHeading({
  icon,
  children,
  section,
  onNavigate,
}: {
  icon: ReactNode;
  children: ReactNode;
  section: string;
  onNavigate?: (section: string) => void;
}) {
  const baseClass = 'flex items-center gap-2 text-lg font-semibold text-gray-900';
  if (!onNavigate) {
    return <h2 className={baseClass}>{icon}{children}</h2>;
  }
  return (
    <button
      type="button"
      onClick={() => onNavigate(section)}
      className={`group ${baseClass} hover:text-ingesta-blue-700 transition-colors`}
      title="Edit in the Details tab"
    >
      {icon}
      <span className="group-hover:underline">{children}</span>
      <ArrowUpRight className="h-4 w-4 text-ingesta-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" />
    </button>
  );
}

// Priority pill styling for the Tasks "up next" list. Mirrors the palette
// used by the task editor in ProjectTasks so the two views read the same.
const TASK_PRIORITY_STYLES: Record<string, { label: string; color: string; bg: string }> = {
  low: { label: 'Low', color: 'text-gray-500', bg: 'bg-gray-100' },
  medium: { label: 'Medium', color: 'text-yellow-700', bg: 'bg-yellow-50' },
  high: { label: 'High', color: 'text-orange-700', bg: 'bg-orange-50' },
  urgent: { label: 'Urgent', color: 'text-red-700', bg: 'bg-red-50' },
};

export function ProjectDashboard({
  project,
  members,
  kpis,
  kpiHistory,
  tasks,
  milestones,
  risks,
  priorities,
  accomplishments,
  isRegeneratingSummary,
  onRegenerateSummary,
  onNavigateToSection,
}: ProjectDashboardProps) {
  const status = healthBadge(project.overall_health);
  const startLabel = formatDate(project.start_date);
  const endLabel = formatDate(project.end_date);
  const remaining = daysRemaining(project.end_date);
  const visibleMembers = members.slice(0, 5);
  const overflow = Math.max(0, members.length - visibleMembers.length);

  // Filter inactive KPIs out of the dashboard view — the Details tab is
  // where KPIs are managed, including activation/deactivation.
  const activeKpis = kpis.filter((k) => k.is_active !== false);

  // Top-N caps for the dashboard cards. Full lists live on Details.
  const topMilestones = milestones.slice(0, 8);
  const topRisks = sortRisks(risks).slice(0, 5);
  const topPriorities = priorities.slice(0, 5);
  const topAccomplishments = accomplishments.slice(0, 5);

  // Task summary + "up next". Cancelled tasks are excluded from the
  // open/complete ratio so they don't drag the percentage. "Up next" is the
  // next handful of unfinished tasks by due date (soonest first, no-date last).
  const completedTaskCount = tasks.filter((t) => t.status === 'complete').length;
  const todoTaskCount = tasks.filter((t) => t.status === 'todo').length;
  const inProgressTaskCount = tasks.filter((t) => t.status === 'in_progress').length;
  const activeTaskCount = tasks.filter((t) => t.status !== 'cancelled').length;
  const taskCompletionPct = activeTaskCount > 0
    ? Math.round((completedTaskCount / activeTaskCount) * 100)
    : 0;
  const upNextTasks = tasks
    .filter((t) => t.status === 'todo' || t.status === 'in_progress')
    .sort((a, b) => {
      if (a.due_date && b.due_date) return a.due_date < b.due_date ? -1 : a.due_date > b.due_date ? 1 : 0;
      if (a.due_date) return -1;
      if (b.due_date) return 1;
      return 0;
    })
    .slice(0, 5);

  // Build a quick lookup so milestone/risk rows can resolve owner_id to
  // a workspace member without re-scanning members on every render.
  const memberById = new Map(members.map((m) => [m.id, m]));

  // Compute the gantt timeline from the project (preferred) or the
  // earliest/latest milestone dates (fallback). When neither is set we
  // skip the gantt entirely.
  const timeline = computeGanttTimeline(project, milestones);
  return (
    <div className="space-y-6">
      {/* Hero header — gradient is brand-anchored and intentionally NOT
          status-tinted (a previous iteration was, but mixing brand blue
          with red/amber read as visually weird). The only status-driven
          colour in the hero is the percent-complete ring on the right. */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-ingesta-blue-600 via-ingesta-blue-700 to-emerald-700 text-white p-8">
        <div className="absolute -top-12 -right-12 w-72 h-72 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -bottom-16 -left-12 w-72 h-72 rounded-full bg-emerald-400/20 blur-3xl" />
        <div className="relative">
          <div className="flex items-start justify-between gap-6">
            <div className="min-w-0">
              <div className="flex items-center gap-3 mb-3">
                {project.external_id && (
                  <span className="px-3 py-1 rounded-full bg-white/20 text-xs font-medium tracking-wide uppercase">
                    {project.external_id}
                  </span>
                )}
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium text-white ${status.pillBg}`}>
                  {status.label}
                </span>
              </div>
              <h1 className="text-4xl font-bold tracking-tight">{project.name}</h1>
              {project.description && (
                <p
                  className="mt-3 text-ingesta-blue-50/90 max-w-3xl text-base leading-relaxed line-clamp-3"
                  title={project.description}
                >
                  {project.description}
                </p>
              )}
              {(startLabel || endLabel || remaining !== null || members.length > 0) && (
                <div className="mt-5 flex flex-wrap items-center gap-5 text-sm text-ingesta-blue-50/80">
                  {(startLabel || endLabel) && (
                    <span className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      {startLabel ?? '—'} → {endLabel ?? '—'}
                    </span>
                  )}
                  {remaining !== null && (
                    <span className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      {remaining === 0
                        ? 'Due today'
                        : remaining === 1
                          ? '1 day remaining'
                          : `${remaining} days remaining`}
                    </span>
                  )}
                  {members.length > 0 && (
                    <div className="flex -space-x-2">
                      {visibleMembers.map((m) => (
                        <div
                          key={m.id}
                          title={m.name ?? m.email}
                          className={`w-7 h-7 rounded-full ${avatarColor(m.id)} ring-2 ring-ingesta-blue-700 flex items-center justify-center text-[10px] font-semibold text-white`}
                        >
                          {avatarInitials(m.name, m.email)}
                        </div>
                      ))}
                      {overflow > 0 && (
                        <div className="w-7 h-7 rounded-full bg-white/15 ring-2 ring-ingesta-blue-700 flex items-center justify-center text-[10px] font-medium text-white">
                          +{overflow}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            <CompletionRing
              pct={project.completion_percentage ?? 0}
              label="Complete"
              fromColor={status.ringFrom}
              toColor={status.ringTo}
            />
          </div>
        </div>
      </div>

      {/* Executive summary card */}
      <div className="rounded-2xl bg-ingesta-blue-50 border border-ingesta-blue-200 p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-ingesta-blue-700 font-medium text-sm">
            <Sparkles className="h-4 w-4" />
            Executive Summary
          </div>
          {onRegenerateSummary && (
            <button
              type="button"
              onClick={onRegenerateSummary}
              disabled={isRegeneratingSummary}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-white border border-ingesta-blue-200 text-sm text-gray-700 hover:bg-ingesta-blue-100 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isRegeneratingSummary ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Regenerating…
                </>
              ) : (
                <>
                  <Sparkles className="h-3.5 w-3.5" />
                  Regenerate
                </>
              )}
            </button>
          )}
        </div>
        {project.executive_summary ? (
          <p className="text-gray-900 leading-relaxed text-[15px]">
            {project.executive_summary}
          </p>
        ) : (
          <p className="text-ingesta-blue-700/70 italic text-sm">
            {onRegenerateSummary
              ? 'No executive summary yet — click Regenerate to have AI write one from the current project state.'
              : 'No executive summary yet.'}
          </p>
        )}
      </div>

      {/* KPI tiles */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <SectionHeading
            icon={<BarChart3 className="h-5 w-5 text-ingesta-teal-500" />}
            section="kpis"
            onNavigate={onNavigateToSection}
          >
            Key Performance Indicators
          </SectionHeading>
        </div>
        {activeKpis.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-6 text-sm text-gray-500 italic">
            No KPIs tracked yet — add some in the Details tab.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {activeKpis.map((kpi, idx) => {
              // Palette is driven by the KPI's own health if set, otherwise
              // we cycle through the four-colour demo palette by index so a
              // page of KPIs reads as varied rather than all-emerald.
              const palette = kpiPalette(paletteForKpi(kpi.health_status, idx));
              const current = kpi.current_value ?? 0;
              const target = kpi.target ?? 0;
              // Lower-is-better unit heuristic: latency (s/ms) and any
              // KPI ending in "load"/"latency" reads as inverse-positive.
              const positiveIsGood = !isLowerBetter(kpi);
              // Progress-toward-target. For lower-is-better KPIs
              // (page-load, latency, errors), beating the target means
              // current ≤ target — show 100%. Otherwise frame progress
              // as target/current so a slower-than-target value reads
              // as "X% of the way to acceptable".
              const pct = target > 0
                ? positiveIsGood
                  ? Math.min(100, Math.round((current / target) * 100))
                  : current <= target
                    ? 100
                    : Math.min(100, Math.round((target / current) * 100))
                : 0;
              const unit = kpi.unit ?? '';
              const history = kpiHistory.get(kpi.id) ?? [];
              // Sparkline values: real history when we have ≥1 point;
              // otherwise [0, current] so empty-history KPIs still render
              // a visual cue. The user explicitly asked for this fallback
              // until kpi_history is populated for existing rows.
              const sparkValues = sparklineValues(history, current);
              // Trend pill: only show when we have at least two real
              // history points to compare. With only [0, current] we
              // hide the pill rather than showing a misleading delta.
              const trend = history.length >= 2
                ? history[history.length - 1].value - history[history.length - 2].value
                : null;
              return (
                <div
                  key={kpi.id}
                  className={`rounded-2xl p-5 ${palette.bg} ${palette.border} border relative overflow-hidden`}
                >
                  <div className="flex items-start justify-between mb-2 gap-2">
                    <p className={`text-xs font-medium uppercase tracking-wider ${palette.label} truncate`}>
                      {kpi.name}
                    </p>
                    {trend !== null && (
                      <TrendBadge value={trend} unit={unit} positiveIsGood={positiveIsGood} />
                    )}
                  </div>
                  <div className="flex items-end justify-between gap-2">
                    <div>
                      <div className={`text-3xl font-bold ${palette.value}`}>
                        {formatKpi(current, unit)}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {target > 0 ? `of ${formatKpi(target, unit)} target` : 'no target set'}
                      </div>
                    </div>
                    <Sparkline values={sparkValues} color={palette.spark} />
                  </div>
                  {target > 0 && (
                    <div className="mt-4 h-1.5 rounded-full bg-white/60 overflow-hidden">
                      <div
                        className={`h-full ${palette.bar}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Milestones gantt */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <SectionHeading
            icon={<Flag className="h-5 w-5 text-ingesta-blue-500" />}
            section="milestones"
            onNavigate={onNavigateToSection}
          >
            Milestones
          </SectionHeading>
          <span className="text-xs text-gray-500">
            {milestones.filter(m => m.status === 'completed').length} of {milestones.length} complete
          </span>
        </div>
        {topMilestones.length === 0 ? (
          <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-500 italic">
            No milestones yet — add some in the Details tab.
          </div>
        ) : (
          <div className="space-y-2">
            {/* Header row: month markers walked from project start to end.
                Hidden when we couldn't compute a timeline (e.g. project
                missing both start_date and end_date and no milestones
                with dates). */}
            {timeline && (
              <div className="grid grid-cols-[200px_1fr_60px] items-center text-xs text-gray-400 px-1 pb-2 border-b border-gray-100">
                <div>Milestone</div>
                <div className="flex">
                  {timeline.monthMarkers.map((m) => (
                    <div key={m.label} className="text-center" style={{ width: `${m.widthPct}%` }}>
                      {m.label}
                    </div>
                  ))}
                </div>
                <div className="text-right">Owner</div>
              </div>
            )}
            {topMilestones.map((m) => {
              const barStatus = m.status ?? 'not_started';
              const ownerMember = m.owner_id ? memberById.get(m.owner_id) : undefined;
              const ownerLabel = ownerMember
                ? avatarInitials(ownerMember.name, ownerMember.email)
                : (m.owner_name ? avatarInitials(m.owner_name, '') : '—');
              const ownerColor = ownerMember
                ? avatarColor(ownerMember.id)
                : 'bg-gray-100';
              const ownerTextWhite = ownerMember ? 'text-white' : 'text-gray-700';
              const range = timeline ? milestoneRange(m, timeline) : null;
              return (
                <div
                  key={m.id}
                  className="grid grid-cols-[200px_1fr_60px] items-center gap-2 px-1 py-1"
                >
                  <div className="flex items-center gap-2 min-w-0 pr-2">
                    <MilestoneStatusDot status={barStatus} />
                    <span className="text-sm text-gray-800 truncate" title={m.name}>{m.name}</span>
                  </div>
                  <div className="relative h-7 bg-gray-50 rounded-md overflow-hidden">
                    {range ? (
                      <div
                        className={`absolute top-1 bottom-1 rounded ${milestoneBarColor(barStatus)}`}
                        style={{ left: `${range.leftPct}%`, right: `${100 - range.rightPct}%` }}
                        title={range.tooltip}
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center px-2 text-[11px] text-gray-400 italic">
                        no dates
                      </div>
                    )}
                  </div>
                  <div className="flex justify-end">
                    <span
                      title={ownerMember?.name ?? ownerMember?.email ?? m.owner_name ?? 'Unassigned'}
                      className={`w-7 h-7 rounded-full ${ownerColor} ring-2 ring-white flex items-center justify-center text-[10px] font-semibold ${ownerTextWhite}`}
                    >
                      {ownerLabel}
                    </span>
                  </div>
                </div>
              );
            })}
            {milestones.length > topMilestones.length && (
              <p className="text-xs text-gray-400 pt-1">
                Showing {topMilestones.length} of {milestones.length}. Manage all in the Details tab.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Risks + Priorities row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Risk heat map */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <SectionHeading
              icon={<AlertTriangle className="h-5 w-5 text-yellow-500" />}
              section="risks"
              onNavigate={onNavigateToSection}
            >
              Risks &amp; Mitigation
            </SectionHeading>
            <span className="text-xs text-gray-500">{risks.length} tracked</span>
          </div>
          {topRisks.length === 0 ? (
            <p className="text-sm text-gray-500 italic">No risks tracked yet — add some in the Details tab.</p>
          ) : (
            <div className="space-y-2">
              {topRisks.map((r) => {
                const ownerMember = r.owner_id ? memberById.get(r.owner_id) : undefined;
                const ownerName = ownerMember?.name ?? ownerMember?.email ?? r.owner_name ?? 'Unassigned';
                return (
                  <div
                    key={r.id}
                    className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <SeverityBadge severity={r.severity} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate" title={r.summary}>{r.summary}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Owner: {ownerName} · Status: <RiskStatus status={r.status} />
                      </p>
                    </div>
                  </div>
                );
              })}
              {risks.length > topRisks.length && (
                <p className="text-xs text-gray-400 pt-1">
                  Showing {topRisks.length} of {risks.length}.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Priorities */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="mb-4">
            <SectionHeading
              icon={<Target className="h-5 w-5 text-blue-500" />}
              section="priorities"
              onNavigate={onNavigateToSection}
            >
              This Week
            </SectionHeading>
          </div>
          {topPriorities.length === 0 ? (
            <p className="text-sm text-gray-500 italic">No priorities yet — add some in the Details tab.</p>
          ) : (
            <ul className="space-y-3">
              {topPriorities.map((p) => (
                <li key={p.id} className="flex gap-3">
                  <div className="flex-shrink-0 w-1 rounded-full bg-blue-500 self-stretch" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-800">{p.content}</p>
                    {p.week_of && (
                      <p className="text-xs text-gray-500 mt-1">Week of {formatDate(p.week_of)}</p>
                    )}
                  </div>
                </li>
              ))}
              {priorities.length > topPriorities.length && (
                <li className="text-xs text-gray-400 pt-1">
                  Showing {topPriorities.length} of {priorities.length}.
                </li>
              )}
            </ul>
          )}
        </div>
      </div>

      {/* Recent activity */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <SectionHeading
            icon={<Zap className="h-5 w-5 text-emerald-500" />}
            section="accomplishments"
            onNavigate={onNavigateToSection}
          >
            Recent Wins
          </SectionHeading>
        </div>
        {topAccomplishments.length === 0 ? (
          <p className="text-sm text-gray-500 italic">No wins logged yet — add some in the Details tab.</p>
        ) : (
          <ul className="space-y-3">
            {topAccomplishments.map((a) => (
              <li key={a.id} className="flex items-start gap-3">
                <div className="mt-0.5 flex-shrink-0">
                  <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Check className="h-4 w-4 text-emerald-600" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-800">{a.content}</p>
                  {a.created_at && (
                    <p className="text-xs text-gray-500 mt-0.5">{formatRelativeTime(a.created_at)}</p>
                  )}
                </div>
              </li>
            ))}
            {accomplishments.length > topAccomplishments.length && (
              <li className="text-xs text-gray-400 pt-1">
                Showing {topAccomplishments.length} of {accomplishments.length}.
              </li>
            )}
          </ul>
        )}
      </div>

      {/* Tasks — open/complete summary + the next few to-dos. Read-only here;
          the full list and editing live in the Details tab. */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <SectionHeading
            icon={<ListTodo className="h-5 w-5 text-ingesta-blue-500" />}
            section="tasks"
            onNavigate={onNavigateToSection}
          >
            Tasks
          </SectionHeading>
          <span className="text-xs text-gray-500">
            {completedTaskCount} of {activeTaskCount} complete
          </span>
        </div>
        {tasks.length === 0 ? (
          <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-500 italic">
            No tasks yet — add or generate them in the Details tab.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Summary tiles */}
            <div className="grid grid-cols-2 gap-3 content-start">
              <div className="rounded-xl bg-gray-50 p-4 text-center">
                <div className="text-2xl font-bold text-gray-700">{todoTaskCount}</div>
                <div className="text-xs text-gray-500 mt-0.5">To Do</div>
              </div>
              <div className="rounded-xl bg-blue-50 p-4 text-center">
                <div className="text-2xl font-bold text-blue-600">{inProgressTaskCount}</div>
                <div className="text-xs text-gray-500 mt-0.5">In Progress</div>
              </div>
              <div className="rounded-xl bg-green-50 p-4 text-center">
                <div className="text-2xl font-bold text-green-600">{completedTaskCount}</div>
                <div className="text-xs text-gray-500 mt-0.5">Complete</div>
              </div>
              <div className="rounded-xl bg-ingesta-blue-50 p-4 text-center">
                <div className="text-2xl font-bold text-ingesta-blue-600">{taskCompletionPct}%</div>
                <div className="text-xs text-gray-500 mt-0.5">Done</div>
              </div>
            </div>

            {/* Up next */}
            <div className="lg:col-span-2">
              <p className="text-xs font-medium uppercase tracking-wider text-gray-400 mb-2">
                Up next
              </p>
              {upNextTasks.length === 0 ? (
                <p className="text-sm text-gray-500 italic">
                  Nothing in the queue — all tasks are complete.
                </p>
              ) : (
                <ul className="space-y-2">
                  {upNextTasks.map((t) => {
                    const pri = TASK_PRIORITY_STYLES[t.priority];
                    return (
                      <li key={t.id} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50">
                        <span className="flex-1 min-w-0 text-sm text-gray-800 truncate" title={t.title}>
                          {t.title}
                        </span>
                        {pri && (
                          <span className={`text-xs px-1.5 py-0.5 rounded hidden sm:inline ${pri.bg} ${pri.color}`}>
                            {pri.label}
                          </span>
                        )}
                        {t.due_date && (
                          <span className="text-xs text-gray-500 flex items-center gap-1 flex-shrink-0">
                            <Calendar className="h-3 w-3" />
                            {formatDate(t.due_date)}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Tiny inline charts -------------------------------------------

function Sparkline({
  values,
  color,
  width = 96,
  height = 32,
}: {
  values: number[];
  color: string;
  width?: number;
  height?: number;
}) {
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const step = width / (values.length - 1);
  const points = values
    .map((v, i) => `${i * step},${height - ((v - min) / range) * (height - 4) - 2}`)
    .join(' ');
  const lastX = (values.length - 1) * step;
  const lastY = height - ((values[values.length - 1] - min) / range) * (height - 4) - 2;
  const fill = `${points} ${lastX},${height} 0,${height}`;
  return (
    <svg width={width} height={height} className="overflow-visible">
      <polygon points={fill} fill={color} opacity={0.12} />
      <polyline points={points} fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={lastX} cy={lastY} r={3} fill={color} />
      <circle cx={lastX} cy={lastY} r={6} fill={color} opacity={0.2} />
    </svg>
  );
}

function CompletionRing({
  pct,
  label,
  fromColor,
  toColor,
}: {
  pct: number;
  label: string;
  fromColor: string;
  toColor: string;
}) {
  const r = 56;
  const c = 2 * Math.PI * r;
  const offset = c - (pct / 100) * c;
  // Unique gradient id per palette so multiple completion rings on the
  // same page (unlikely today, but cheap to be safe) don't collide.
  const gradId = `ringGrad-${fromColor.slice(1)}-${toColor.slice(1)}`;
  return (
    <div className="relative w-40 h-40 flex items-center justify-center">
      <svg width="160" height="160" className="-rotate-90">
        <defs>
          <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={fromColor} />
            <stop offset="100%" stopColor={toColor} />
          </linearGradient>
        </defs>
        {/* Track + label colours assume the ring sits on the dark hero
            background. A pure-gray track (#e5e7eb) read as a stark
            white-gray donut against the teal, and gray-900 text on the
            same dark hero was barely legible — the "too light + too
            dark at the same time" problem. Translucent white track and
            white text are properly inverted for a dark surface. */}
        <circle cx="80" cy="80" r={r} stroke="rgba(255, 255, 255, 0.22)" strokeWidth="14" fill="none" />
        <circle
          cx="80"
          cy="80"
          r={r}
          stroke={`url(#${gradId})`}
          strokeWidth="14"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-4xl font-bold text-white">{pct}%</span>
        <span className="text-xs uppercase tracking-wider text-white/80 mt-1">{label}</span>
      </div>
    </div>
  );
}

// ---------- Helpers ------------------------------------------------------

function formatDate(d: string | null): string | null {
  if (!d) return null;
  const date = new Date(d);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function daysRemaining(endDate: string | null): number | null {
  if (!endDate) return null;
  const end = new Date(endDate);
  if (isNaN(end.getTime())) return null;
  const diffMs = end.getTime() - Date.now();
  return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
}

function avatarInitials(name: string | null, email: string): string {
  const trimmed = name?.trim();
  if (trimmed) {
    const parts = trimmed.split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return email.slice(0, 2).toUpperCase();
}

// Tailwind classes are used directly so the JIT compiler keeps them in the
// final bundle — runtime-built class strings can be tree-shaken.
const AVATAR_COLORS = [
  'bg-rose-500',
  'bg-amber-500',
  'bg-violet-500',
  'bg-sky-500',
  'bg-emerald-500',
  'bg-indigo-500',
  'bg-pink-500',
  'bg-orange-500',
  'bg-cyan-500',
  'bg-fuchsia-500',
];

function avatarColor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) & 0x7fffffff;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

function healthBadge(health: 'green' | 'yellow' | 'red') {
  switch (health) {
    case 'green':
      return {
        label: 'On Track',
        // Solid status colour with white text — no opacity overlays, no
        // pulsing dot. Reads as a confident state indicator on the
        // brand-blue hero background.
        pillBg: 'bg-emerald-500',
        // Completion-ring stops — the only other status-driven colour
        // in the hero. Hero gradient itself stays brand-anchored.
        ringFrom: '#34d399', // emerald-400
        ringTo: '#047857',   // emerald-700
      };
    case 'yellow':
      return {
        label: 'At Risk',
        pillBg: 'bg-amber-500',
        ringFrom: '#fbbf24', // amber-400
        ringTo: '#b45309',   // amber-700
      };
    case 'red':
      return {
        label: 'Off Track',
        pillBg: 'bg-red-500',
        ringFrom: '#f87171', // red-400
        ringTo: '#b91c1c',   // red-700
      };
  }
}

function kpiPalette(color: string) {
  switch (color) {
    case 'emerald':
      return {
        bg: 'bg-emerald-50',
        border: 'border-emerald-100',
        label: 'text-emerald-700',
        value: 'text-emerald-900',
        bar: 'bg-emerald-500',
        spark: '#10b981',
      };
    case 'sky':
      return {
        bg: 'bg-sky-50',
        border: 'border-sky-100',
        label: 'text-sky-700',
        value: 'text-sky-900',
        bar: 'bg-sky-500',
        spark: '#0ea5e9',
      };
    case 'violet':
      return {
        bg: 'bg-violet-50',
        border: 'border-violet-100',
        label: 'text-violet-700',
        value: 'text-violet-900',
        bar: 'bg-violet-500',
        spark: '#8b5cf6',
      };
    case 'amber':
    default:
      return {
        bg: 'bg-amber-50',
        border: 'border-amber-100',
        label: 'text-amber-700',
        value: 'text-amber-900',
        bar: 'bg-amber-500',
        spark: '#f59e0b',
      };
  }
}

function formatKpi(n: number, unit: string): string {
  if (unit === '%') return `${n}%`;
  if (unit === 's') return `${n.toFixed(1)}s`;
  if (n >= 1000) return n.toLocaleString();
  return n.toString();
}

function TrendBadge({
  value,
  unit,
  positiveIsGood,
}: {
  value: number;
  unit: string;
  positiveIsGood: boolean;
}) {
  const isUp = value > 0;
  const good = positiveIsGood ? isUp : !isUp;
  const Icon = isUp ? TrendingUp : TrendingDown;
  const colorClass = good ? 'text-emerald-700 bg-emerald-100' : 'text-red-700 bg-red-100';
  const sign = isUp ? '+' : '';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${colorClass}`}>
      <Icon className="h-3 w-3" />
      {sign}{formatTrendValue(value)}{unit && unit !== 's' ? unit : ''}
    </span>
  );
}

/** Round trend deltas to a sane precision. JS floating-point subtraction
 *  on KPI history values (e.g. 1.28 − 1.20) produces tails like
 *  "-0.08000000000000007" — useless and ugly in a pill. Whole numbers
 *  stay whole; everything else rounds to 1 decimal place. */
function formatTrendValue(value: number): string {
  if (Number.isInteger(value)) return value.toString();
  return (Math.round(value * 10) / 10).toString();
}

// Real schema enum: 'not_started' | 'in_progress' | 'completed' | 'overdue'.
function MilestoneStatusDot({ status }: { status: string }) {
  if (status === 'completed')
    return <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />;
  if (status === 'in_progress')
    return <Circle className="h-4 w-4 text-sky-500 fill-sky-500 flex-shrink-0" />;
  if (status === 'overdue')
    // Amber caution triangle — matches the marketing demo. Red read too
    // harsh and competed with the actual risk pills below the gantt.
    return <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />;
  return <Circle className="h-4 w-4 text-gray-300 flex-shrink-0" />;
}

function milestoneBarColor(status: string): string {
  switch (status) {
    case 'completed':
      return 'bg-gradient-to-r from-emerald-400 to-emerald-500';
    case 'in_progress':
      return 'bg-gradient-to-r from-sky-400 to-sky-500';
    case 'overdue':
      return 'bg-gradient-to-r from-amber-400 to-amber-500';
    default:
      return 'bg-gray-200';
  }
}

function SeverityBadge({ severity }: { severity: string }) {
  const cfg: Record<string, { bg: string; text: string; label: string }> = {
    low: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'LOW' },
    medium: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'MED' },
    high: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'HIGH' },
    critical: { bg: 'bg-red-100', text: 'text-red-700', label: 'CRIT' },
  };
  const c = cfg[severity] ?? cfg.medium;
  return (
    <span className={`flex-shrink-0 px-2 py-1 rounded-md text-[10px] font-bold tracking-wider ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  );
}

function RiskStatus({ status }: { status: string }) {
  const colorMap: Record<string, string> = {
    open: 'text-red-600',
    mitigating: 'text-amber-600',
    mitigated: 'text-emerald-600',
    closed: 'text-gray-500',
  };
  return <span className={colorMap[status] ?? 'text-gray-600'}>{status}</span>;
}

// ---------- KPI helpers ---------------------------------------------------

const KPI_PALETTES: Array<'emerald' | 'sky' | 'violet' | 'amber'> = [
  'emerald',
  'sky',
  'violet',
  'amber',
];

/** Choose a tile palette for a KPI. yellow + red are explicit "this KPI is
 *  in trouble" signals and override the palette to amber. green/null are
 *  the default state and cycle through the demo palettes (emerald, sky,
 *  violet, amber) by index so a page of KPIs reads as varied rather than
 *  monotone — matching the visual rhythm of the marketing dashboard. */
function paletteForKpi(
  health: 'green' | 'yellow' | 'red' | null | undefined,
  index: number
): string {
  if (health === 'yellow' || health === 'red') return 'amber';
  return KPI_PALETTES[index % KPI_PALETTES.length];
}

/** Build the value array a Sparkline expects. Falls back to [0, current]
 *  when there's no recorded history, per design — so empty-history KPIs
 *  still draw a meaningful line until kpi_history is populated. */
function sparklineValues(history: KpiHistoryPoint[], current: number): number[] {
  if (history.length === 0) return [0, current];
  if (history.length === 1) return [0, history[0].value];
  return history.map((p) => p.value);
}

/** Heuristic: KPI is "lower is better" when the unit is a time unit or
 *  the name reads like a latency/error/cost metric. Drives whether a
 *  negative trend pill renders green or red. */
function isLowerBetter(kpi: Tables<'kpis'>): boolean {
  const u = (kpi.unit ?? '').toLowerCase();
  if (u === 's' || u === 'ms' || u === 'sec' || u === 'seconds') return true;
  const n = kpi.name.toLowerCase();
  return /latency|load|response|error|defect|bug|churn|cost/.test(n);
}

// ---------- Risk helpers --------------------------------------------------

const SEVERITY_RANK: Record<string, number> = {
  critical: 4,
  high: 3,
  medium: 2,
  low: 1,
};

function sortRisks<T extends Tables<'risks'>>(risks: T[]): T[] {
  return [...risks].sort((a, b) => {
    const sevDiff = (SEVERITY_RANK[b.severity] ?? 0) - (SEVERITY_RANK[a.severity] ?? 0);
    if (sevDiff !== 0) return sevDiff;
    const at = a.created_at ? new Date(a.created_at).getTime() : 0;
    const bt = b.created_at ? new Date(b.created_at).getTime() : 0;
    return bt - at;
  });
}

// ---------- Gantt helpers -------------------------------------------------

interface GanttTimeline {
  startMs: number;
  endMs: number;
  totalMs: number;
  monthMarkers: { label: string; widthPct: number }[];
}

/** Compute the gantt timeline from project dates (preferred) or, when
 *  the project is missing dates, the earliest/latest milestone dates.
 *  Returns null when no usable dates exist anywhere — caller hides the
 *  month header in that case. Generates monthly tick labels sized
 *  proportionally to each month's share of the total span (so a 14-day
 *  February doesn't get the same label width as a 31-day March). */
function computeGanttTimeline(
  project: { start_date: string | null; end_date: string | null },
  milestones: Tables<'milestones'>[]
): GanttTimeline | null {
  const candidateStarts: number[] = [];
  const candidateEnds: number[] = [];
  if (project.start_date) candidateStarts.push(new Date(project.start_date).getTime());
  if (project.end_date) candidateEnds.push(new Date(project.end_date).getTime());
  for (const m of milestones) {
    if (m.start_date) candidateStarts.push(new Date(m.start_date).getTime());
    if (m.due_date) candidateEnds.push(new Date(m.due_date).getTime());
  }
  candidateStarts.filter((n) => !isNaN(n));
  candidateEnds.filter((n) => !isNaN(n));
  if (candidateStarts.length === 0 || candidateEnds.length === 0) return null;
  const startMs = Math.min(...candidateStarts);
  const endMs = Math.max(...candidateEnds);
  if (endMs <= startMs) return null;
  const totalMs = endMs - startMs;

  // Walk by month from the first-of-month at startMs through endMs,
  // building tick segments sized proportional to each month's overlap.
  const markers: { label: string; widthPct: number }[] = [];
  const cursor = new Date(startMs);
  cursor.setDate(1);
  while (cursor.getTime() <= endMs) {
    const monthStart = Math.max(cursor.getTime(), startMs);
    const next = new Date(cursor);
    next.setMonth(next.getMonth() + 1);
    const monthEnd = Math.min(next.getTime(), endMs);
    const widthPct = ((monthEnd - monthStart) / totalMs) * 100;
    if (widthPct > 0) {
      markers.push({
        label: cursor.toLocaleDateString('en-US', { month: 'short' }),
        widthPct,
      });
    }
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return { startMs, endMs, totalMs, monthMarkers: markers };
}

/** Compute the left/right percentage offsets for a milestone bar within
 *  the project timeline. Falls back to a 5%-wide marker centred on
 *  due_date when only one date is set. Returns null when the milestone
 *  has no dates at all (caller renders a "no dates" placeholder). */
function milestoneRange(
  m: Tables<'milestones'>,
  timeline: GanttTimeline
): { leftPct: number; rightPct: number; tooltip: string } | null {
  const startSrc = m.start_date ?? m.due_date;
  const endSrc = m.due_date ?? m.start_date;
  if (!startSrc || !endSrc) return null;
  const startMs = new Date(startSrc).getTime();
  const endMs = new Date(endSrc).getTime();
  if (isNaN(startMs) || isNaN(endMs)) return null;

  const rawLeft = ((startMs - timeline.startMs) / timeline.totalMs) * 100;
  const rawRight = ((endMs - timeline.startMs) / timeline.totalMs) * 100;
  let leftPct = Math.max(0, Math.min(100, rawLeft));
  let rightPct = Math.max(0, Math.min(100, rawRight));
  if (rightPct - leftPct < 2) {
    // Single-point or near-zero range — give it a 2% wide marker so
    // it's visible. Centre on the due date.
    const centre = rightPct;
    leftPct = Math.max(0, centre - 1);
    rightPct = Math.min(100, centre + 1);
  }

  const startLabel = formatDate(m.start_date) ?? formatDate(m.due_date) ?? '—';
  const endLabel = formatDate(m.due_date) ?? formatDate(m.start_date) ?? '—';
  return {
    leftPct,
    rightPct,
    tooltip: `${startLabel} → ${endLabel}`,
  };
}

// ---------- Misc ----------------------------------------------------------

function formatRelativeTime(iso: string): string {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return '';
  }
}
